import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc, 
  serverTimestamp,
  deleteDoc,
  limit
} from 'firebase/firestore';
import { db } from './firebase';
import { addUserToSalon } from './userRoles';
import { createUserAccount } from './auth';
import { nanoid } from 'nanoid';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from './firebase';

/**
 * Gera um token único para convite
 * @returns {string} Token de convite
 */
export const generateInvitationToken = () => {
  return nanoid(12); // Gera um ID de 12 caracteres
};

/**
 * Cria um convite para um usuário
 * @param {string} salonId - ID do salão
 * @param {string} email - Email do convidado
 * @param {string} role - Papel do usuário ("manager" | "professional")
 * @param {Array} permissions - Permissões (apenas para gestores)
 * @param {Object} professionalData - Dados do profissional (apenas para profissionais)
 * @param {string} invitedBy - ID do usuário que enviou o convite
 * @returns {Promise<Object>} - Dados do convite criado
 */
export const createUserInvitation = async (salonId, email, role, permissions = [], professionalData = null, invitedBy) => {
  try {
    // Verificar se já existe um convite ativo para este email neste salão
    const existingInvitation = await getUserInvitationByEmail(salonId, email);
    
    if (existingInvitation) {
      // Se já existe, atualizar o convite
      return updateUserInvitation(existingInvitation.id, salonId, {
        role,
        permissions,
        professionalData,
        updatedAt: serverTimestamp(),
        status: 'pending',
        invitedBy
      });
    }
    
    // Gerar token único
    const invitationToken = generateInvitationToken();
    
    // Criar documento de convite
    const invitationRef = doc(collection(db, 'invitations'));
    
    const invitationData = {
      id: invitationRef.id,
      token: invitationToken,
      salonId,
      email: email.toLowerCase(),
      role,
      permissions: role === 'manager' ? permissions : [],
      professionalData: role === 'professional' ? professionalData : null,
      status: 'pending', // pending, accepted, expired
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 dias
      invitedBy
    };
    
    await setDoc(invitationRef, invitationData);
    
    return {
      ...invitationData,
      createdAt: new Date(),
      updatedAt: new Date(),
      expiresAt: invitationData.expiresAt
    };
  } catch (error) {
    console.error('Erro ao criar convite:', error);
    throw error;
  }
};

/**
 * Atualiza um convite existente
 * @param {string} invitationId - ID do convite
 * @param {string} salonId - ID do salão
 * @param {Object} data - Dados a serem atualizados
 * @returns {Promise<Object>} - Dados do convite atualizado
 */
export const updateUserInvitation = async (invitationId, salonId, data) => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId);
    
    await updateDoc(invitationRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    // Buscar os dados atualizados
    const updatedInvitation = await getDoc(invitationRef);
    
    return {
      ...updatedInvitation.data()
    };
  } catch (error) {
    console.error('Erro ao atualizar convite:', error);
    throw error;
  }
};

/**
 * Busca um convite pelo token
 * @param {string} token - Token de convite
 * @returns {Promise<Object|null>} - Dados do convite ou null se não encontrado
 */
export const getUserInvitationByToken = async (token) => {
  try {
    const invitationsRef = collection(db, 'invitations');
    const q = query(invitationsRef, where('token', '==', token), limit(1));
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const invitation = querySnapshot.docs[0].data();
    
    // Verificar se o convite expirou
    const now = new Date();
    const expiresAt = invitation.expiresAt instanceof Date 
      ? invitation.expiresAt 
      : new Date(invitation.expiresAt.seconds * 1000);
    
    if (now > expiresAt && invitation.status === 'pending') {
      // Marcar convite como expirado
      await updateDoc(doc(db, 'invitations', invitation.id), {
        status: 'expired',
        updatedAt: serverTimestamp()
      });
      
      invitation.status = 'expired';
    }
    
    return invitation;
  } catch (error) {
    console.error('Erro ao buscar convite por token:', error);
    throw error;
  }
};

/**
 * Busca um convite pelo email
 * @param {string} salonId - ID do salão
 * @param {string} email - Email do convidado
 * @returns {Promise<Object|null>} - Dados do convite ou null se não encontrado
 */
export const getUserInvitationByEmail = async (salonId, email) => {
  try {
    const invitationsRef = collection(db, 'invitations');
    const q = query(
      invitationsRef, 
      where('salonId', '==', salonId),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending'),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    return querySnapshot.docs[0].data();
  } catch (error) {
    console.error('Erro ao buscar convite por email:', error);
    throw error;
  }
};

/**
 * Lista todos os convites pendentes de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de convites
 */
export const getPendingInvitations = async (salonId) => {
  try {
    const invitationsRef = collection(db, 'invitations');
    const q = query(
      invitationsRef,
      where('salonId', '==', salonId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Erro ao listar convites pendentes:', error);
    throw error;
  }
};

/**
 * Aceita um convite e cria uma conta de usuário
 * @param {string} token - Token de convite
 * @param {string} displayName - Nome do usuário
 * @param {string} password - Senha do usuário
 * @returns {Promise<Object>} - Dados do usuário criado e do salão associado
 */
export const acceptInvitation = async (token, displayName, password) => {
  try {
    // Verificar se o convite existe e é válido
    const invitation = await getUserInvitationByToken(token);
    
    if (!invitation) {
      throw new Error('Convite não encontrado ou inválido');
    }
    
    if (invitation.status !== 'pending') {
      throw new Error('Este convite já foi utilizado ou expirou');
    }
    
    const expiryDate = invitation.expiresAt instanceof Date 
      ? invitation.expiresAt 
      : invitation.expiresAt.toDate();
      
    if (expiryDate < new Date()) {
      throw new Error('Este convite expirou');
    }
    
    console.log(`[acceptInvitation] Processando convite para email: ${invitation.email}, role: ${invitation.role}`);
    
    // Verificar se já existe um profissional com este email no salão
    let existingProfessional = null;
    if (invitation.role === 'professional') {
      const { getProfessionalByEmail } = await import('./professionals');
      existingProfessional = await getProfessionalByEmail(invitation.salonId, invitation.email);
      console.log(`[acceptInvitation] Verificação de profissional existente:`, 
        existingProfessional ? `Encontrado com ID ${existingProfessional.id}` : 'Não encontrado');
    }
    
    // Criar ou buscar usuário
    let userCredential;
    let userId;
    
    try {
      // Verificar se o usuário já existe tentando fazer login
      console.log(`[acceptInvitation] Tentando fazer login com email: ${invitation.email}`);
      userCredential = await signInWithEmailAndPassword(auth, invitation.email, password);
      userId = userCredential.user.uid;
      console.log(`[acceptInvitation] Usuário já existe. ID: ${userId}`);
      
      // Atualizar o perfil do usuário existente
      await updateProfile(userCredential.user, {
        displayName: displayName
      });
    } catch (error) {
      // Se não existe, criar um novo usuário
      console.log(`[acceptInvitation] Usuário não existe. Criando novo usuário para: ${invitation.email}`);
      userCredential = await createUserWithEmailAndPassword(auth, invitation.email, password);
      userId = userCredential.user.uid;
      
      // Atualizar o perfil do usuário
      console.log(`[acceptInvitation] Atualizando perfil para usuário: ${userId}`);
      await updateProfile(userCredential.user, {
        displayName: displayName
      });
    }
    
    // Criar documento na coleção 'users'
    try {
      console.log(`[acceptInvitation] Criando documento na coleção 'users' para ID: ${userId}`);
      
      // Verificar se o documento já existe
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        console.log(`[acceptInvitation] Documento de usuário já existe. Atualizando.`);
        await updateDoc(userDocRef, {
          displayName: displayName,
          updatedAt: serverTimestamp()
        });
      } else {
        console.log(`[acceptInvitation] Criando novo documento de usuário.`);
        await setDoc(userDocRef, {
          email: invitation.email,
          displayName: displayName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (userDocError) {
      console.error(`[acceptInvitation] Erro ao criar documento de usuário:`, userDocError);
      // Continuar mesmo com erro para não interromper o fluxo, mas logar o erro
    }
    
    // Adicionar usuário ao salão com o papel definido no convite
    if (invitation.role === 'professional') {
      if (existingProfessional) {
        // Se o profissional já existe, apenas vincular o usuário a ele
        console.log(`[acceptInvitation] Atualizando profissional existente (ID: ${existingProfessional.id}) para vincular ao usuário ${userId}`);
        
        const { updateProfessional } = await import('./professionals');
        await updateProfessional(invitation.salonId, existingProfessional.id, {
          userId: userId,
          name: displayName, // Atualizar nome se necessário
          updatedAt: serverTimestamp()
        });
        
        // Associar usuário ao salão como profissional
        console.log(`[acceptInvitation] Associando usuário ${userId} ao salão ${invitation.salonId} com professionalId ${existingProfessional.id}`);
        
        await addUserToSalon(
          userId,
          invitation.salonId,
          'professional',
          ['view_own_schedule', 'view_own_commissions', 'manage_own_profile'],
          existingProfessional.id
        );
      } else {
        // Se o profissional não existe, criar um novo
        const professionalData = {
          ...(invitation.professionalData || {}),
          userId: userId,
          name: displayName,
          email: invitation.email.toLowerCase()
        };
        
        console.log(`[acceptInvitation] Criando novo profissional para o usuário ${userId} no salão ${invitation.salonId}`, professionalData);
        
        const { addProfessional } = await import('./professionals');
        const professional = await addProfessional(invitation.salonId, professionalData);
        
        console.log(`[acceptInvitation] Profissional criado com ID: ${professional.id}`);
        
        // Verificar se o professional.id existe
        if (!professional || !professional.id) {
          console.error(`[acceptInvitation] Falha ao criar profissional: objeto professional inválido`, professional);
          throw new Error('Falha ao criar profissional');
        }
        
        // Associar usuário ao salão como profissional
        console.log(`[acceptInvitation] Associando usuário ${userId} ao salão ${invitation.salonId} com professionalId ${professional.id}`);
        
        await addUserToSalon(
          userId,
          invitation.salonId,
          'professional',
          ['view_own_schedule', 'view_own_commissions', 'manage_own_profile'],
          professional.id
        );
      }
    } else {
      // Para gestores
      console.log(`[acceptInvitation] Associando usuário como ${invitation.role} ao salão: ${invitation.salonId}`);
      await addUserToSalon(
        userId,
        invitation.salonId,
        invitation.role,
        invitation.permissions
      );
    }
    
    // Atualizar status do convite
    console.log(`[acceptInvitation] Atualizando status do convite: ${invitation.id}`);
    await updateDoc(doc(db, 'invitations', invitation.id), {
      status: 'accepted',
      updatedAt: serverTimestamp(),
      acceptedAt: serverTimestamp(),
      acceptedBy: userId
    });
    
    // Buscar dados do salão
    const salonRef = doc(db, 'salons', invitation.salonId);
    const salonSnap = await getDoc(salonRef);
    
    console.log(`[acceptInvitation] Processo concluído com sucesso para: ${invitation.email}`);
    
    return {
      user: userCredential.user,
      salon: {
        id: invitation.salonId,
        ...salonSnap.data()
      },
      role: invitation.role
    };
  } catch (error) {
    console.error('[acceptInvitation] Erro ao aceitar convite:', error);
    throw error;
  }
};

/**
 * Cancela um convite
 * @param {string} invitationId - ID do convite
 * @returns {Promise<boolean>} - true se o convite foi cancelado
 */
export const cancelInvitation = async (invitationId) => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId);
    const invitationSnap = await getDoc(invitationRef);
    
    if (!invitationSnap.exists()) {
      return false;
    }
    
    const invitation = invitationSnap.data();
    
    if (invitation.status !== 'pending') {
      return false;
    }
    
    await deleteDoc(invitationRef);
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar convite:', error);
    throw error;
  }
};

/**
 * Reenviar um convite, atualizando a data de expiração
 * @param {string} invitationId - ID do convite
 * @returns {Promise<Object>} - Dados do convite atualizado
 */
export const resendInvitation = async (invitationId) => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId);
    const invitationSnap = await getDoc(invitationRef);
    
    if (!invitationSnap.exists()) {
      throw new Error('Convite não encontrado');
    }
    
    const invitation = invitationSnap.data();
    
    if (invitation.status !== 'pending' && invitation.status !== 'expired') {
      throw new Error('Não é possível reenviar um convite já aceito');
    }
    
    // Gerar novo token
    const newToken = generateInvitationToken();
    
    // Atualizar convite
    await updateDoc(invitationRef, {
      token: newToken,
      status: 'pending',
      updatedAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 dias
    });
    
    // Buscar dados atualizados
    const updatedSnap = await getDoc(invitationRef);
    
    return updatedSnap.data();
  } catch (error) {
    console.error('Erro ao reenviar convite:', error);
    throw error;
  }
}; 